// src/components/graphs/DefectTrendOverMonths.js

import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import { AuthContext } from 'contexts/auth_context/AuthContext';
import axios from 'axios';
import { BASE_URL } from 'BaseURL';
import { COLORS } from 'constants/colors';
import Spinner from 'components/Spinner';
import 'chartjs-plugin-datalabels'; 
import './ChartStyles.css';

const DefectTrendOverMonths = ({ filter }) => {
    const [chartData, setChartData] = useState(null);
    const [view, setView] = useState({
        type: 'main',
        defectCode: null,
        monthIndex: null,
    });
    const { token } = useContext(AuthContext);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        const fetchDefectTrend = async () => {
            try {
                const params = {
                    ...(filter.vehicleModel && { vehicle_model: filter.vehicleModel }),
                    ...(filter.startDate && { start_date: filter.startDate }),
                    ...(filter.endDate && { end_date: filter.endDate }),
                };

                const response = await axios.get(
                    `${BASE_URL}shiftlens_warehouse/api/defect-trend/`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        params,
                    }
                );

                setData(response.data);
            } catch {
                setError('Failed to load data.');
            }
        };

        fetchDefectTrend();
    }, [token, filter]);

    useEffect(() => {
        if (!data) return;

        let processedData = { labels: [], datasets: [] };

        if (view.type === 'main') {
            processedData = processMainDefects(data);
        } else if (view.type === 'sub') {
            processedData = processSubDefects(data, view.defectCode, view.monthIndex);
        }

        if (processedData.labels && processedData.datasets) {
            setChartData({
                labels: processedData.labels,
                datasets: processedData.datasets,
            });
        } else {
            setChartData(null);
        }
    }, [data, view]);

    const processMainDefects = (data) => {
        const months = data.map((item) => item.month);
        const defectCounts = {};

        data.forEach((item, monthIndex) => {
            Object.entries(item.defects).forEach(([defectCode, count]) => {
                if (!defectCounts[defectCode])
                    defectCounts[defectCode] = Array(data.length).fill(0);
                defectCounts[defectCode][monthIndex] += count;
            });
        });

        const datasets = createDatasets(defectCounts);

        return { labels: months, datasets };
    };

    const processSubDefects = (data, mainDefectCode, monthIndex) => {
        const monthData = data[monthIndex];
        if (!monthData) {
            return { labels: [], datasets: [] };
        }

        const labels = [monthData.month]; // Only one month label
        const subDefectCounts = {}; // { subDefectCode: count }

        const mainDefectCodePrefix = mainDefectCode.split(' - ')[0].trim();

        Object.entries(monthData.sub_defects || {}).forEach(([defectCode, count]) => {
            const subDefectCodePrefix = defectCode.split(' - ')[0].trim();
            if (subDefectCodePrefix.startsWith(mainDefectCodePrefix)) {
                subDefectCounts[defectCode] = count;
            }
        });

        const datasets = Object.entries(subDefectCounts).map(
            ([defectCode, count], index) => ({
                label: defectCode,
                data: [count], // Only one data point
                backgroundColor: COLORS[index % COLORS.length],
            })
        );

        if (datasets.length === 0) {
            datasets.push({
                label: 'No Subdefects Found',
                data: [0],
                backgroundColor: COLORS[0],
            });
        }

        return { labels, datasets };
    };

    const createDatasets = (defectCounts) =>
        Object.entries(defectCounts).map(([defectCode, counts], index) => ({
            label: defectCode,
            data: counts,
            backgroundColor: COLORS[index % COLORS.length],
        }));

    const onBarClick = useCallback(
        (event, elements) => {
            if (elements.length > 0 && view.type === 'main') {
                const { datasetIndex, index } = elements[0];
                const defectCode = chartData.datasets[datasetIndex].label;
                const monthIndex = index;
                setView({ type: 'sub', defectCode, monthIndex });
            }
        },
        [chartData, view.type]
    );

    const handleBackClick = () =>
        setView({ type: 'main', defectCode: null, monthIndex: null });

    const options = useMemo(
        () => ({
            onClick: onBarClick,
            maintainAspectRatio: false,
            responsive: true,
            animation: {
                duration: 500,
                easing: 'easeInOutQuad',
            },
            plugins: {
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                        label: function (context) {
                            const value = context.raw;
                            if (value === 0) {
                                return ''; // Hide tooltip for zero values
                            }
                            return `${context.dataset.label}: ${value}`;
                        },
                    },
                },
                legend: {
                    position: 'top',
                    labels: {
                        boxWidth: 15,
                        font: {
                            size: 12,
                        },
                    },
                },
                datalabels: {
                    display: function (context) {
                        return context.dataset.data[context.dataIndex] !== 0;
                    },
                    color: 'black',
                    align: 'center',
                    anchor: 'center',
                    font: {
                        weight: 'bold',
                        size: 12,
                    },
                    formatter: (value) => `${value}`,
                },
            },
            scales: {
                x: {
                    stacked: view.type === 'main',
                    title: {
                        display: true,
                        text: 'Month',
                        font: {
                            size: 14,
                        },
                    },
                    ticks: {
                        autoSkip: false,
                        maxRotation: 45,
                        minRotation: 0,
                        font: {
                            size: 12,
                        },
                    },
                },
                y: {
                    stacked: view.type === 'main',
                    title: {
                        display: true,
                        text: 'Number of Defects',
                        font: {
                            size: 14,
                        },
                    },
                    beginAtZero: true,
                    ticks: {
                        font: {
                            size: 12,
                        },
                        callback: function (value) {
                            if (value === 0) {
                                return ''; // Hide '0' tick label
                            }
                            return value;
                        },
                    },
                },
            },
        }),
        [onBarClick, view.type]
    );

    return (
        <div className="chart-wrapper">
            {view.type === 'sub' && (
                <button className="back-button" onClick={handleBackClick}>
                    ← Back to Main Defects
                </button>
            )}
            {error && <p className="error-message">{error}</p>}
            {chartData ? (
                <Bar data={chartData} options={options} />
            ) : (
                <Spinner />
            )}
        </div>
    );
};

export default React.memo(DefectTrendOverMonths);
